export const ADD_PLACE_DRAFT_SCHEMA_VERSION = 1;
export const ADD_PLACE_DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export const defaultAddPlaceForm = {
  name: "", tipo: "", categories: [], location: "", city: "", state: "",
  latitude: null, longitude: null, selectedPlace: false, maps: "", gplace_id: "",
  telefono: "", platillo: "", lo_bueno: [], extras: [], imagenes: [], sabor: 4,
  precio: 3, servicio: 4, satisfaccion: 4, total_bill: "", party_size: "",
  precio_label: "", chef_msg: "", staff_msg: "", comentario: "",
};

const text = (value, max = 1000) => String(value ?? "").slice(0, max);
const stringList = (value, maxItems = 20, maxLength = 120) => Array.isArray(value)
  ? value.map(item => text(item, maxLength)).filter(Boolean).slice(0, maxItems)
  : [];
const coordinate = value => Number.isFinite(Number(value)) ? Number(value) : null;
const rating = (value, fallback) => Math.min(7, Math.max(1, Number(value) || fallback));

const normalizeImages = value => Array.isArray(value) ? value.slice(0, 12).map(image => {
  if (typeof image === "string") return text(image, 2000);
  if (!image || typeof image !== "object") return null;
  const url = text(image.url || image.secure_url, 2000);
  if (!url) return null;
  return { ...image, url, name: text(image.name, 240) };
}).filter(Boolean) : [];

export const normalizeAddPlaceForm = value => ({
  ...defaultAddPlaceForm,
  name: text(value?.name, 200),
  tipo: text(value?.tipo, 120),
  categories: stringList(value?.categories, 14, 120),
  location: text(value?.location, 500),
  city: text(value?.city, 160),
  state: text(value?.state, 160),
  latitude: coordinate(value?.latitude),
  longitude: coordinate(value?.longitude),
  selectedPlace: !!value?.selectedPlace,
  maps: text(value?.maps, 2000),
  gplace_id: text(value?.gplace_id, 300),
  telefono: text(value?.telefono, 80),
  platillo: text(value?.platillo, 500),
  lo_bueno: stringList(value?.lo_bueno),
  extras: stringList(value?.extras),
  imagenes: normalizeImages(value?.imagenes),
  sabor: rating(value?.sabor, 4),
  precio: rating(value?.precio, 3),
  servicio: rating(value?.servicio, 4),
  satisfaccion: rating(value?.satisfaccion, 4),
  total_bill: text(value?.total_bill, 20),
  party_size: text(value?.party_size, 10),
  precio_label: text(value?.precio_label, 160),
  chef_msg: text(value?.chef_msg, 80),
  staff_msg: text(value?.staff_msg, 80),
  comentario: text(value?.comentario, 2000),
});

export const addPlaceDraftKey = userId => `venaqqi_add_place_draft_${text(userId || "guest", 120)}`;

export const normalizeAddPlaceDraft = (value, userId, now = Date.now()) => {
  if (!value || typeof value !== "object") return null;
  const savedAt = Number(value.savedAt) || 0;
  const owner = text(value.owner || userId || "guest", 120);
  if (owner !== text(userId || "guest", 120)) return null;
  if (!savedAt || now < savedAt || now - savedAt > ADD_PLACE_DRAFT_MAX_AGE_MS) return null;
  return {
    schemaVersion: ADD_PLACE_DRAFT_SCHEMA_VERSION,
    owner,
    form: normalizeAddPlaceForm(value.form),
    step: Math.min(2, Math.max(1, Number(value.step) || 1)),
    savedAt,
  };
};

export const readAddPlaceDraft = (userId, storage = window.localStorage, now = Date.now()) => {
  let parsed = null;
  try { parsed = JSON.parse(storage.getItem(addPlaceDraftKey(userId)) || "null"); } catch { /* corrupted draft */ }
  return normalizeAddPlaceDraft(parsed, userId, now);
};

export const writeAddPlaceDraft = (userId, form, step, storage = window.localStorage, now = Date.now()) => {
  const draft = {
    schemaVersion: ADD_PLACE_DRAFT_SCHEMA_VERSION,
    owner: text(userId || "guest", 120),
    form: normalizeAddPlaceForm(form),
    step: Math.min(2, Math.max(1, Number(step) || 1)),
    savedAt: now,
  };
  try { storage.setItem(addPlaceDraftKey(userId), JSON.stringify(draft)); } catch { /* storage unavailable */ }
  return draft;
};

export const clearAddPlaceDraft = (userId, storage = window.localStorage) => {
  try { storage.removeItem(addPlaceDraftKey(userId)); } catch { /* storage unavailable */ }
};

export const hasMeaningfulAddPlaceDraft = form => {
  const normalized = normalizeAddPlaceForm(form);
  return [normalized.name, normalized.tipo, normalized.location, normalized.maps, normalized.platillo, normalized.comentario]
    .some(value => String(value).trim())
    || normalized.categories.length > 0
    || normalized.imagenes.length > 0;
};
